import React, { useState } from "react";
import { Box, Typography, Button, TextField, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, Divider, Snackbar, Alert } from "@mui/material";
import { Visibility, VisibilityOff, Close } from "@mui/icons-material";
import { labelStyles, formFieldStyles } from "../../styles/formStyles";
import { changePassword } from "../../services/UserProfile";
import { IChangePasswordDTO } from "../../interfaces/UserProfile";

interface ChangePasswordProps {
  open: boolean;
  onClose: () => void;
}

const ChangePassword = ({ open, onClose }: ChangePasswordProps) => {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState<any>({});
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  const validate = () => {
    const newErrors: any = {};
    if (!formData.currentPassword) newErrors.currentPassword = "Current password is required";
    if (!formData.newPassword) newErrors.newPassword = "New password is required";
    if (!formData.confirmPassword) newErrors.confirmPassword = "Confirm password is required";
    if (formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const dto: IChangePasswordDTO = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      };

      const response = await changePassword(dto);

      if (response && response.status === "success") {
        setSnackbarMessage("Password changed successfully");
        setSnackbarSeverity("success");
        setShowSnackbar(true);
        setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setTimeout(() => onClose(), 1500);
      } else {
        const errorMsg = response?.errorResponse?.response?.data?.message || response?.errorResponse?.message || "Failed to change password";
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.error("Change password error:", err);
      setSnackbarMessage(err?.message || "An error occurred while changing password");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };

  const handleClose = () => {
    setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Change Password</Typography>
          <Typography variant="body2" color="text.secondary">Enter your current password and new password to change it.</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography component="label" sx={labelStyles}>
              Current Password <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              type={showCurrent ? "text" : "password"}
              placeholder="Enter current password"
              fullWidth
              value={formData.currentPassword}
              onChange={(e) => {
                setFormData({ ...formData, currentPassword: e.target.value });
                setErrors({ ...errors, currentPassword: "" });
              }}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword}
              sx={formFieldStyles}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrent(!showCurrent)} edge="end">
                      {showCurrent ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box>
            <Typography component="label" sx={labelStyles}>
              New Password <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              type={showNew ? "text" : "password"}
              placeholder="Enter new password"
              fullWidth
              value={formData.newPassword}
              onChange={(e) => {
                setFormData({ ...formData, newPassword: e.target.value });
                setErrors({ ...errors, newPassword: "" });
              }}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              sx={formFieldStyles}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNew(!showNew)} edge="end">
                      {showNew ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box>
            <Typography component="label" sx={labelStyles}>
              Confirm New Password <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              type={showConfirm ? "text" : "password"}
              placeholder="Confirm new password"
              fullWidth
              value={formData.confirmPassword}
              onChange={(e) => {
                setFormData({ ...formData, confirmPassword: e.target.value });
                setErrors({ ...errors, confirmPassword: "" });
              }}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              sx={formFieldStyles}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button 
            variant="outlined" 
            onClick={handleClose}
            sx={{
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
              padding: "8px 16px",
              textTransform: "none"
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            sx={{
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
              padding: "8px 40px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "primary.main",
                boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset"
              }
            }}
          >
            Update Password
          </Button>
        </Box>
      </DialogContent>
      
      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: `${snackbarSeverity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ChangePassword;
